import React from "react";
import { useQuery } from "@tanstack/react-query";
import {
  AccessControlGroupResponse,
  AccessControlGroupType,
  AccessScheduleResponse,
  SiteSystemType,
} from "api/ApiClient";
import { apiClientFactory } from "helpers";
import { Check } from "lucide-react";
import { AdminTable } from "components/shared/AdminTable";
import { Alert } from "components/ui/alert";
import { Button } from "components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "components/ui/select";

type AccessScheduleWithSite = AccessScheduleResponse & {
  siteId: string;
  siteName?: string;
  name?: string;
  entries?: AccessScheduleEntry[];
};

type ScheduleOption = {
  optionValue: string;
  scheduleId?: string;
  label: string;
  disabled?: boolean;
};

type AccessScheduleEntry = {
  id?: string;
  time?: number;
  length?: number;
  dayOfWeek?: number | string;
};

type AccessScheduleDisplayRow = {
  time: number;
  length: number;
  days: [boolean, boolean, boolean, boolean, boolean, boolean, boolean];
};

type SchlageSite = {
  id: string;
  name?: string;
};

const NO_SCHEDULE_VALUE = "__no_schedule__";

export default function GroupSchedules({
  groupId,
  group,
}: {
  groupId: string;
  group: AccessControlGroupResponse;
}) {
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [selectedScheduleBySiteId, setSelectedScheduleBySiteId] = React.useState<Record<string, string>>({});
  const [initialSelectedScheduleBySiteId, setInitialSelectedScheduleBySiteId] =
    React.useState<Record<string, string>>({});

  const existingIds = React.useMemo(
    () => (group.accessSchedules || []).map((schedule) => schedule.id),
    [group.accessSchedules],
  );

  const { data: groupLocksData = [], isLoading: loadingGroupLocks } = useQuery({
    queryKey: ["acg", "schedules", "locks", groupId],
    queryFn: () => apiClientFactory().getLocksForAccessControlGroup(groupId, 0, 2000, undefined),
    enabled: !!groupId,
  });

  const schlageSites = React.useMemo(() => {
    const schlageSiteById = new Map<string, SchlageSite>();

    groupLocksData.forEach((groupLock) => {
      const site = groupLock.lock?.site;
      if (!site?.id || site.account?.systemType !== SiteSystemType.SchlageEngage) {
        return;
      }

      if (!schlageSiteById.has(site.id)) {
        schlageSiteById.set(site.id, {
          id: site.id,
          name: site.name,
        });
      }
    });

    return Array.from(schlageSiteById.values());
  }, [groupLocksData]);

  const { data: scheduleData = [], isLoading: loadingSchedules } = useQuery({
    queryKey: ["acg", "schedules", "all", groupId, schlageSites.map((site) => site.id).join(",")],
    queryFn: async () => {
      const response = await Promise.all(
        schlageSites.map((site) =>
          apiClientFactory().getAccessSchedules(site.id).then((schedules) =>
            schedules.map((schedule) => ({
              ...schedule,
              siteId: site.id,
              siteName: site.name,
            } as AccessScheduleWithSite)),
          ),
        ),
      );

      return response.flat();
    },
    enabled: schlageSites.length > 0,
  });

  const schedulesBySiteId = React.useMemo(() => {
    const grouped: Record<string, AccessScheduleWithSite[]> = {};

    scheduleData.forEach((schedule) => {
      if (!grouped[schedule.siteId]) {
        grouped[schedule.siteId] = [];
      }
      grouped[schedule.siteId].push(schedule);
    });

    return grouped;
  }, [scheduleData]);

  const editableScheduleIds = React.useMemo(
    () => new Set(scheduleData.map((schedule) => schedule.id).filter(Boolean) as string[]),
    [scheduleData],
  );

  const preservedScheduleIds = React.useMemo(
    () => existingIds.filter((id): id is string => !!id && !editableScheduleIds.has(id)),
    [editableScheduleIds, existingIds],
  );

  React.useEffect(() => {
    if (!scheduleData.length) {
      return;
    }

    const nextSelection: Record<string, string> = {};
    Object.entries(schedulesBySiteId).forEach(([siteId, schedules]) => {
      const existingForSite = schedules.find((schedule) =>
        schedule.id ? existingIds.includes(schedule.id) : false,
      );
      nextSelection[siteId] = existingForSite?.id || "";
    });

    setSelectedScheduleBySiteId(nextSelection);
    setInitialSelectedScheduleBySiteId(nextSelection);
  }, [existingIds, scheduleData, schedulesBySiteId]);

  const handleSave = async () => {
    setSaving(true);
    setError(null);

    try {
      const selectedScheduleIds = Object.values(selectedScheduleBySiteId).filter(
        (scheduleId): scheduleId is string => !!scheduleId,
      );

      await apiClientFactory().putAccessControlGroupSchedules(
        groupId,
        [...preservedScheduleIds, ...selectedScheduleIds],
      );
      setInitialSelectedScheduleBySiteId(selectedScheduleBySiteId);
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || "Unable to save schedules.");
    } finally {
      setSaving(false);
    }
  };

  const timeNumberToAmPmString = (time?: number) => {
    const hours24 = Math.floor((time || 0) / 60);
    const minutes = (time || 0) % 60;
    const period = hours24 >= 12 ? "PM" : "AM";
    const hours12 = hours24 % 12 || 12;
    return `${hours12}:${minutes.toString().padStart(2, "0")} ${period}`;
  };

  const getScheduleName = (schedule: AccessScheduleWithSite, index: number) => {
    const trimmedName = schedule.name?.trim();
    return trimmedName || `Schedule ${index + 1}`;
  };

  const getScheduleOptions = (siteSchedules: AccessScheduleWithSite[]): ScheduleOption[] => {
    return siteSchedules.map((schedule, index) => {
      if (!schedule.id) {
        return {
          optionValue: `missing-id-${index}`,
          label: `${getScheduleName(schedule, index)} (Unavailable)`,
          disabled: true,
        };
      }

      return {
        optionValue: schedule.id,
        scheduleId: schedule.id,
        label: getScheduleName(schedule, index),
      };
    });
  };

  const getDayOfWeekIndex = (dayOfWeek?: number | string): number | null => {
    if (typeof dayOfWeek === "number" && dayOfWeek >= 0 && dayOfWeek <= 6) {
      return dayOfWeek;
    }

    if (typeof dayOfWeek === "string") {
      const dayOfWeekMap: Record<string, number> = {
        sunday: 0,
        monday: 1,
        tuesday: 2,
        wednesday: 3,
        thursday: 4,
        friday: 5,
        saturday: 6,
      };

      const normalizedDay = dayOfWeek.trim().toLowerCase();
      if (dayOfWeekMap[normalizedDay] !== undefined) {
        return dayOfWeekMap[normalizedDay];
      }
    }

    return null;
  };

  const getScheduleDisplayRows = (schedule: AccessScheduleWithSite): AccessScheduleDisplayRow[] => {
    if (schedule.entries && schedule.entries.length > 0) {
      const groupedRows = new Map<string, AccessScheduleDisplayRow>();

      schedule.entries.forEach((entry) => {
        const time = entry.time || 0;
        const length = entry.length || 0;
        const key = `${time}-${length}`;

        if (!groupedRows.has(key)) {
          groupedRows.set(key, {
            time,
            length,
            days: [false, false, false, false, false, false, false],
          });
        }

        const dayOfWeekIndex = getDayOfWeekIndex(entry.dayOfWeek);
        if (dayOfWeekIndex !== null) {
          groupedRows.get(key)!.days[dayOfWeekIndex] = true;
        }
      });

      return Array.from(groupedRows.values()).sort(
        (a, b) => a.time - b.time || a.length - b.length,
      );
    }

    return [
      {
        time: schedule.time || 0,
        length: schedule.length || 0,
        days: [
          !!schedule.sunday,
          !!schedule.monday,
          !!schedule.tuesday,
          !!schedule.wednesday,
          !!schedule.thursday,
          !!schedule.friday,
          !!schedule.saturday,
        ],
      },
    ];
  };

  const hasPendingChanges = React.useMemo(
    () =>
      schlageSites.some(
        (site) =>
          (selectedScheduleBySiteId[site.id] || "") !==
          (initialSelectedScheduleBySiteId[site.id] || ""),
      ),
    [initialSelectedScheduleBySiteId, schlageSites, selectedScheduleBySiteId],
  );

  if (loadingGroupLocks || loadingSchedules) {
    return <div>loading...</div>;
  }

  if (!schlageSites.length) {
    return (
      <Alert variant="info">
        No Schlage sites were found in this group&apos;s locks, so schedules cannot be configured here.
      </Alert>
    );
  }

  return (
    <div>
      {group.type === AccessControlGroupType.Master ? (
        <Alert className="mb-2" variant="warning">
          A master access group cannot have restrictive schedules.
        </Alert>
      ) : null}

      {error ? <Alert className="mb-2" variant="destructive">{error}</Alert> : null}

      {schlageSites.map((site) => {
        const siteSchedules = schedulesBySiteId[site.id] || [];
        const scheduleOptions = getScheduleOptions(siteSchedules);
        const selectedScheduleId = selectedScheduleBySiteId[site.id] || "";
        const selectedSchedule = siteSchedules.find((schedule) => schedule.id === selectedScheduleId);
        const selectedScheduleValue = selectedScheduleId || NO_SCHEDULE_VALUE;

        return (
          <div key={site.id} style={{ marginBottom: 20 }}>
            <div className="mb-1 grid max-w-sm gap-2">
              <label htmlFor={`group-schedule-select-${site.id}`}>
                Schedule ({site.name || site.id})
              </label>
              <Select
                value={selectedScheduleValue}
                disabled={group.type === AccessControlGroupType.Master}
                onValueChange={(value) =>
                  setSelectedScheduleBySiteId((prev) => ({
                    ...prev,
                    [site.id]: value === NO_SCHEDULE_VALUE ? "" : value,
                  }))
                }
              >
                <SelectTrigger id={`group-schedule-select-${site.id}`} className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NO_SCHEDULE_VALUE}>None</SelectItem>
                  {scheduleOptions.map((option) => (
                    <SelectItem key={option.optionValue} value={option.optionValue} disabled={option.disabled}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {selectedSchedule ? (
              <AdminTable
                aria-label={`Schedule entries for ${site.name || site.id}`}
                containerClassName="mt-3"
              >
                <thead>
                  <tr>
                    <th scope="col">Start</th>
                    <th scope="col">End</th>
                    <th scope="col">S</th>
                    <th scope="col">M</th>
                    <th scope="col">T</th>
                    <th scope="col">W</th>
                    <th scope="col">T</th>
                    <th scope="col">F</th>
                    <th scope="col">S</th>
                  </tr>
                </thead>
                <tbody>
                  {getScheduleDisplayRows(selectedSchedule).map((entry, index) => (
                    <tr key={`${selectedSchedule.id || "schedule"}-${index}`}>
                      <td>{timeNumberToAmPmString(entry.time)}</td>
                      <td>{timeNumberToAmPmString(entry.time + entry.length)}</td>
                      <td>{entry.days[0] ? <Check className="size-4" /> : "—"}</td>
                      <td>{entry.days[1] ? <Check className="size-4" /> : "—"}</td>
                      <td>{entry.days[2] ? <Check className="size-4" /> : "—"}</td>
                      <td>{entry.days[3] ? <Check className="size-4" /> : "—"}</td>
                      <td>{entry.days[4] ? <Check className="size-4" /> : "—"}</td>
                      <td>{entry.days[5] ? <Check className="size-4" /> : "—"}</td>
                      <td>{entry.days[6] ? <Check className="size-4" /> : "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </AdminTable>
            ) : (
              <Alert className="mt-3" variant="info">
                No schedule selected for {site.name || site.id}.
              </Alert>
            )}
          </div>
        );
      })}

      <Button
        onClick={handleSave}
        disabled={saving || !hasPendingChanges || group.type === AccessControlGroupType.Master}
        type="button"
      >
        {saving ? "Saving..." : "Save Changes"}
      </Button>
    </div>
  );
}
